import React from 'react';
import {
  formatKesPrice,
  formatTierDescription,
  formatTierPriceDisplay,
  getPublicPricingTiers,
  sortPricingTiersByPrice
} from '../../utils/eventFormat';

const PackageQuantityControl = ({ quantity, max, onChange, disabled, compact = false }) => {
  const btnClass = compact
    ? 'w-7 h-7 rounded-md border border-gray-300 flex items-center justify-center text-gray-700 text-sm hover:border-amber-500 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed'
    : 'w-9 h-9 rounded-lg border border-gray-300 flex items-center justify-center text-gray-700 hover:border-amber-500 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className={`flex items-center shrink-0 ${compact ? 'gap-1' : 'gap-2'}`}>
      <button
        type="button"
        disabled={disabled || quantity <= 0}
        onClick={() => onChange(Math.max(0, quantity - 1))}
        className={btnClass}
        aria-label="Decrease quantity"
      >
        −
      </button>
      <span className={`text-center font-semibold text-gray-900 tabular-nums ${compact ? 'w-5 text-xs' : 'w-8'}`}>
        {quantity}
      </span>
      <button
        type="button"
        disabled={disabled || (max != null && quantity >= max)}
        onClick={() => onChange(max != null ? Math.min(max, quantity + 1) : quantity + 1)}
        className={btnClass}
        aria-label="Increase quantity"
      >
        +
      </button>
    </div>
  );
};

const TicketPackageSelector = ({
  event,
  tierQuantities,
  onQuantityChange,
  disabled = false,
  compact = false
}) => {
  const tiers = sortPricingTiersByPrice(getPublicPricingTiers(event.pricingTiers), event.price);
  const hasPackages = tiers.length > 0;

  const cartTotal = hasPackages
    ? tiers.reduce((sum, tier) => {
        const qty = tierQuantities[tier.id] || 0;
        return sum + Number(tier.price) * qty;
      }, 0)
    : 0;

  const cartCount = hasPackages
    ? Object.values(tierQuantities).reduce((sum, qty) => sum + qty, 0)
    : 0;

  if (!hasPackages) {
    return null;
  }

  if (compact) {
    return (
      <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
        {tiers.map((tier) => {
          const qty = tierQuantities[tier.id] || 0;
          const maxSelectable = tier.remaining ?? tier.maxPerOrder ?? tier.maxQuantity ?? 10;
          const description = tier.description ? formatTierDescription(tier.description) : null;
          const { priceLabel, originalLabel, hasDiscount } = formatTierPriceDisplay(tier);

          return (
            <div
              key={tier.id}
              className={`px-2.5 py-2 ${qty > 0 ? 'bg-slate-50/80' : 'bg-white'}`}
            >
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-gray-900 leading-tight truncate">
                    {tier.name}
                  </p>
                  {description && (
                    <p className="text-[11px] text-gray-500 leading-snug line-clamp-1 mt-0.5">
                      {description}
                    </p>
                  )}
                </div>
                <span className="text-[13px] font-bold text-amber-700 whitespace-nowrap shrink-0 text-right">
                  {hasDiscount && (
                    <span className="block text-[10px] font-medium text-gray-400 line-through leading-none mb-0.5">
                      {originalLabel}
                    </span>
                  )}
                  {priceLabel}
                </span>
                <PackageQuantityControl
                  compact
                  quantity={qty}
                  max={maxSelectable}
                  disabled={disabled || tier.remaining === 0}
                  onChange={(next) => onQuantityChange(tier.id, next)}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-bold text-gray-900">Choose your tickets</h3>
        <p className="text-sm text-gray-500 mt-1">Select quantities for each package below.</p>
      </div>

      <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
        {tiers.map((tier) => {
          const qty = tierQuantities[tier.id] || 0;
          const lineTotal = Number(tier.price) * qty;
          const maxSelectable = tier.remaining ?? tier.maxPerOrder ?? tier.maxQuantity ?? 10;
          const description = tier.description ? formatTierDescription(tier.description) : null;
          const { priceLabel, originalLabel, hasDiscount } = formatTierPriceDisplay(tier);

          return (
            <div
              key={tier.id}
              className={`px-4 py-3 ${qty > 0 ? 'bg-slate-50/80' : 'bg-white'}`}
            >
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 leading-tight">{tier.name}</p>
                  {description && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{description}</p>
                  )}
                </div>
                <span className="font-bold text-amber-700 whitespace-nowrap shrink-0 text-right">
                  {hasDiscount && (
                    <span className="block text-xs font-medium text-gray-400 line-through leading-none mb-0.5">
                      {originalLabel}
                    </span>
                  )}
                  {priceLabel}
                </span>
                <PackageQuantityControl
                  quantity={qty}
                  max={maxSelectable}
                  disabled={disabled || tier.remaining === 0}
                  onChange={(next) => onQuantityChange(tier.id, next)}
                />
              </div>
              {qty > 0 && (
                <div className="mt-2 flex justify-end text-xs text-gray-600">
                  Subtotal: <span className="font-semibold text-gray-900 ml-1">{formatKesPrice(lineTotal)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Order total</p>
          <p className="text-xs text-gray-400">{cartCount} ticket{cartCount === 1 ? '' : 's'} selected</p>
        </div>
        <p className="text-2xl font-bold text-amber-700">{formatKesPrice(cartTotal)}</p>
      </div>
    </div>
  );
};

export default TicketPackageSelector;
